import {
  isPricingProductsEqual,
  MATERIAL_TYPES,
  TILE_SIZES_ROTATION,
  type PricingProduct,
} from '@mixtiles/web-backend-shared'

type LineItem = { pricingProduct?: PricingProduct }

// The same unframed tile is described two different ways depending on where the
// pricingProduct comes from:
//   - The live tile / client checkout summary uses getTileMaterialType, which
//     reports an unframed tile as `frameless`.
//   - A cart order reads the *persisted* order, where the order builder shipped
//     the tile's raw materialType (`classic`).
// Both describe the same physical product (an unframed tile is priced and
// manufactured as classic-with-no-frame), but hashTilePricingProduct hashes
// `frameless` and `classic` differently, so the tile never matches its own line
// item and the lookup returns undefined. Collapse frameless to classic before
// comparing. Matching-only — it does not touch the order record, pricing, or
// fulfillment.
const normalizeFramelessToClassic = (
  pricingProduct: PricingProduct
): PricingProduct =>
  pricingProduct.materialType === MATERIAL_TYPES.FRAMELESS
    ? { ...pricingProduct, materialType: MATERIAL_TYPES.CLASSIC }
    : pricingProduct

// Matches a tile's pricing product against a price-summary line item, tolerating
// the frameless/classic representation gap above and the landscape/portrait
// tile-size rotation (e.g. 16X12 vs 12X16).
export const isTileEqualToLineItem =
  (tilePricingProduct: PricingProduct) =>
  (lineItem: LineItem): boolean => {
    if (!lineItem.pricingProduct) return false

    const tileProduct = normalizeFramelessToClassic(tilePricingProduct)
    const lineItemProduct = normalizeFramelessToClassic(lineItem.pricingProduct)

    return (
      isPricingProductsEqual(tileProduct, lineItemProduct) ||
      isPricingProductsEqual(tileProduct, {
        ...lineItemProduct,
        tileSize: TILE_SIZES_ROTATION[lineItemProduct.tileSize],
      })
    )
  }
